/**
 * Ausschreibungs-Board — the joined view of a tender and the BPM process running on it.
 *
 * Everything shown here comes from one server call (`GET /:customer/tender-board`), which does the
 * join across two databases and resolves the German phase labels. This component deliberately holds
 * **no** copy of the phase vocabulary, the attention thresholds or the activity labels — duplicating
 * them here is how the two halves drift apart.
 *
 * The summary counts come from the server too, computed over the UNFILTERED rows: a filter chip has
 * to show what selecting it would yield, so counting after filtering would make each chip report its
 * own result.
 *
 * Layout (Wireframe 19.10.2026, `tri-ux/wireframe/board-wireframe.html`): a focus line on top
 * („Heute zu sichten: …"), quick-filter chips, expired tenders hidden by default, one filter bar,
 * one status label per row, an Unterlagen column, labelled hover actions and a batch bar for a
 * selection. Actions and dialogs live in `TenderBoardActions.tsx`.
 *
 * A click on a row opens the tender in a side panel (`TenderDetailPanel.tsx`) instead of expanding
 * it in place: the board stays behind it, filters, selection and scroll position untouched. The open
 * tender is in the address (`?tender=<id>`), so a link — or „In neuem Tab öffnen“ — lands on it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import { AlertTriangle, FileText, Loader2, Plus, RefreshCw, Search } from "lucide-react";

import DataTable from "@/components/fluent-datatable-compat";
import { Dropdown } from "@/components/fluent-dropdown-compat";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { dataTableStyles } from "../../styles";
import { userAPI } from "../../UserAPI";
import { authenticationAtom } from "../../State";
import { useAdministrationAPI } from "../../AdministrationAPI";
import {
  useTenderSearchProfileAPI,
  TenderBoardRow,
  TenderBoardSummary,
  TenderBoardFocus,
  TenderBoardQuickFilter,
} from "../../TenderSearchProfileAPI";
import { AddTenderDialog } from "./AddTenderDialog";
import { TenderDetailPanel } from "./TenderDetailPanel";
import { BatchBar, BoardActionDialog, BoardDialog, RowActions } from "./TenderBoardActions";
import { describeDeadline } from "./tenderDeadline";
import { isNetworkError } from "./tenderPanelText";
import { ExistingCustomerBadge } from "./ExistingCustomerBadge";
import { FitLight } from "./FitLight";
import { matchedProfileNames, TenderProfileBadges } from "./TenderProfileBadges";

type BoardView = "all" | "process" | "triage";
type SortKey = "deadline" | "newest" | "fit";

/** The chips, in the order the wireframe shows them. */
const QUICK_FILTERS: TenderBoardQuickFilter[] = [
  "new",
  "dueThisWeek",
  "noDocuments",
  "noOwner",
  "existingCustomer",
  "fitGreen",
  "fitDisputed",
];

const QUICK_COUNT: Record<TenderBoardQuickFilter, keyof TenderBoardFocus> = {
  new: "new",
  dueThisWeek: "dueThisWeek",
  noDocuments: "withoutDocuments",
  noOwner: "noOwner",
  existingCustomer: "existingCustomer",
  fitGreen: "fitGreen",
  fitDisputed: "fitDisputed",
};

/** Same shape the panel already uses for its role dropdowns. */
interface Role {
  key: string;
  text: string;
}

interface Props {
  /** Every role of the customer — the Weitergabe target may be any of them. */
  roles: Role[];
  /**
   * Role ids referenced by the tender search profiles: the departments that actually receive
   * tenders. The Bereich filter is narrowed to these, because a customer has ~30 roles
   * ("Sprinklerwart", "ZÜS", …) of which two are relevant here, and a 30-item filter is noise.
   * Handover keeps the full list — you may legitimately hand to a department that has no profile.
   */
  tenderRoleIds?: string[];
  /** Profile id → name of the customer's own search profiles, to name the profiles a row hit. */
  profileNames?: ReadonlyMap<string, string>;
  /** Bumped by the parent to force a reload. */
  refreshSignal?: number;
}

/** Remembered per browser: open a clicked row in a new tab instead of the panel. */
const NEW_TAB_KEY = "tenderBoard.openInNewTab";

const readNewTab = (): boolean => {
  try {
    return window.localStorage.getItem(NEW_TAB_KEY) === "1";
  } catch {
    return false;
  }
};

/** Only http(s) links leave the portal — the URL comes from a crawled notice. */
const safeExternalUrl = (url: string | null): string | null =>
  url && /^https?:\/\//i.test(url) ? url : null;

const TERMINAL_PROCESS = new Set(["COMPLETED", "TERMINATED", "FAILED"]);

/** Display name of an owner id (an e-mail address): the user's name when known, else the local part. */
const ownerName = (owner: string, people: Record<string, string>): string =>
  people[owner] ?? owner.replace(/@.*$/, "");

const initialsOf = (name: string): string =>
  name
    .split(/[\s.\-_+]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const NO_PROFILE_NAMES: ReadonlyMap<string, string> = new Map();

/** Title, buyer and place, with the markers that belong to the tender itself. */
function TenderCell({
  row,
  people,
  profileNames,
  onFitFeedback,
}: {
  row: TenderBoardRow;
  people?: Record<string, string>;
  profileNames?: ReadonlyMap<string, string>;
  onFitFeedback?: (row: TenderBoardRow, verdict: "fits" | "notFits" | null) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  return (
    <div className="py-1">
      <div className="flex items-baseline gap-2 text-sm font-semibold leading-snug">
        {row.isNew && (
          <span
            className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-primary"
            title={t("tenderBoard.quick.new")}
            aria-label={t("tenderBoard.quick.new")}
          />
        )}
        <span>{row.title}</span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>{[row.buyer, row.place].filter(Boolean).join(" · ") || "—"}</span>
        <FitLight
          fit={row.fit}
          feedback={row.fitFeedback}
          people={people}
          onFeedback={onFitFeedback ? (verdict) => onFitFeedback(row, verdict) : undefined}
        />
        <ExistingCustomerBadge row={row} />
        <TenderProfileBadges
          names={matchedProfileNames(row.profileIds, profileNames ?? NO_PROFILE_NAMES)}
          multiProfile={row.multiProfile}
        />
        {row.siblingProcessCount > 0 && (
          <span className="rounded border border-red-600 bg-red-50 px-1 py-px text-[10px] text-red-800">
            {t("tenderBoard.siblingProcesses", { count: row.siblingProcessCount })}
          </span>
        )}
      </div>
    </div>
  );
}

/** One status label, with what is open underneath — instead of a phase plus a step. */
function StatusCell({ row }: { row: TenderBoardRow }) {
  const { t } = useTranslation();
  const finished = row.process && TERMINAL_PROCESS.has(String(row.process.status ?? "").toUpperCase());
  const below = row.openTask
    ? t("tenderBoard.status.open", { task: row.openTask })
    : row.process
      ? finished
        ? t("tenderBoard.status.finished")
        : t("tenderBoard.status.automatic")
      : null;
  const tone =
    row.status.key === "verworfen"
      ? "border-dashed border-muted-foreground/50 text-muted-foreground"
      : row.status.key === "ergebnis" || row.status.key === "abgegeben"
        ? "border-emerald-600 bg-emerald-50 text-emerald-800"
        : "border-border bg-muted text-foreground";
  return (
    <div className="flex flex-col gap-1 py-1">
      <span className={`w-fit rounded border px-2 py-0.5 text-xs font-semibold ${tone}`}>
        {row.status.label}
      </span>
      {below && <span className="text-xs text-muted-foreground">{below}</span>}
    </div>
  );
}

/** How many files we hold; a warning with the platform link when the crawl found none. */
function DocumentsCell({ row }: { row: TenderBoardRow }) {
  const { t } = useTranslation();
  const count = row.documents?.count ?? null;
  const url = safeExternalUrl(row.documents?.url ?? null);
  if (count === null) {
    // Never crawled: unknown, not missing — neutral, so the amber warning keeps its meaning.
    return (
      <span
        className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
        title={t("tenderBoard.documents.pendingHint")}
      >
        {t("tenderBoard.documents.pending")}
      </span>
    );
  }
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-amber-600 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-900">
        <AlertTriangle size={12} aria-hidden />
        {t("tenderBoard.documents.missing")}
        {url && (
          <>
            {" – "}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-amber-700"
              onClick={(event) => event.stopPropagation()}
            >
              {t("tenderBoard.documents.openLink")}
            </a>
          </>
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <FileText size={14} className="text-muted-foreground" aria-hidden />
      {t("tenderBoard.documents.count", { count })}
    </span>
  );
}

/** Initials avatar. "Niemand" reads as a state, not as a gap. */
function OwnerCell({ row, people }: { row: TenderBoardRow; people: Record<string, string> }) {
  const { t } = useTranslation();
  const owner = row.ownerUserId ?? row.currentStep?.assigneeUserId ?? null;
  if (!owner) {
    return (
      <span className="flex items-center gap-2 text-sm italic text-muted-foreground">
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-[10px] not-italic">
          ?
        </span>
        {t("tenderBoard.owner.nobody")}
      </span>
    );
  }
  const name = ownerName(owner, people);
  return (
    <span className="flex items-center gap-2" title={owner}>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[10px] font-bold">
        {initialsOf(name) || "?"}
      </span>
      <span className="text-sm">{name}</span>
      {!row.ownerUserId && (
        <span className="text-[10px] text-muted-foreground">{t("tenderBoard.owner.claimedOnly")}</span>
      )}
    </span>
  );
}

/** The date, and underneath how far away it is in words; the next seven days stand out. */
function DeadlineCell({ row, now }: { row: TenderBoardRow; now: Date }) {
  const { t } = useTranslation();
  const view = describeDeadline(row.deadline, now, {
    today: (time) => (time ? t("tenderBoard.deadline.todayAt", { time }) : t("tenderBoard.deadline.today")),
    tomorrow: (time) =>
      time ? t("tenderBoard.deadline.tomorrowAt", { time }) : t("tenderBoard.deadline.tomorrow"),
    inDays: (days, time) =>
      time ? t("tenderBoard.deadline.inDaysAt", { days, time }) : t("tenderBoard.deadline.inDaysPlain", { days }),
    past: (days) => t("tenderBoard.deadline.past", { count: days }),
    pastToday: (time) =>
      time ? t("tenderBoard.deadline.pastTodayAt", { time }) : t("tenderBoard.deadline.pastToday"),
  });
  if (!view) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="whitespace-nowrap tabular-nums">
      <div className={`text-sm font-medium ${view.past ? "text-muted-foreground line-through" : ""}`}>
        {view.date}
      </div>
      <div
        className={`text-xs ${
          view.highlight ? "font-semibold text-primary" : "text-muted-foreground"
        }`}
      >
        {view.relative}
      </div>
    </div>
  );
}

export default function TenderBoard({ roles, tenderRoleIds, profileNames, refreshSignal = 0 }: Props) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useTenderSearchProfileAPI();
  const { getUsers } = useAdministrationAPI();

  const [rows, setRows] = useState<TenderBoardRow[]>([]);
  const [summary, setSummary] = useState<TenderBoardSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Triage is where a day starts; "In Bearbeitung" and "Alle" are one click away.
  const [view, setView] = useState<BoardView>("triage");
  const [quick, setQuick] = useState<TenderBoardQuickFilter | null>(null);
  const [showExpired, setShowExpired] = useState(false);
  const [roleId, setRoleId] = useState<string>("");
  /** "" = everyone, "me" = the current user, otherwise an owner's e-mail address. */
  const [owner, setOwner] = useState<string>("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("deadline");

  // The open tender lives in the address, so a link (or a new tab) opens the same panel.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("tender");
  const setOpenId = (id: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id) next.set("tender", id);
        else next.delete("tender");
        return next;
      },
      { replace: true }
    );
  const [openInNewTab, setOpenInNewTab] = useState(readNewTab);
  /** Bumped after a board dialog changed the open tender, so the panel reloads its trail. */
  const [panelRefresh, setPanelRefresh] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [selected, setSelected] = useState<TenderBoardRow[]>([]);
  /** Flipped to clear the table's own selection (the compat table's `clearSelectedRows` contract). */
  const [clearSelection, setClearSelection] = useState(false);
  const [dialog, setDialog] = useState<BoardDialog | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);
  /** E-mail → display name, from the user list when this user may read it. */
  const [people, setPeople] = useState<Record<string, string>>({});

  const currentUser = authentication?.email ?? authentication?.user?.email ?? null;

  // Deployed workflows a triage tender can be started on. Loaded once: the list changes only when a
  // workflow is deployed, and the triage row is the only consumer.
  const [definitions, setDefinitions] = useState<{ key: string; text: string }[]>([]);

  // Only the newest request may write the rows: typing fires one load per key, and a slower earlier
  // answer arriving last used to leave the list filtered by an older search (Testbericht: "Suche
  // filtert gelegentlich erst nach erneutem Tippen").
  const latestLoad = useRef(0);

  // Departments that actually receive tenders: those named by a search profile, plus any a tender
  // has already been handed to (so a handover target never disappears from the filter afterwards).
  const departmentRoles = useMemo(() => {
    const relevant = new Set([
      ...(tenderRoleIds ?? []),
      ...rows.map((row) => row.responsibleRoleId).filter(Boolean as any as (v: string | null) => v is string),
    ]);
    // No profile roles configured yet → show everything rather than an empty filter.
    if (!relevant.size) return roles;
    return roles.filter((role) => relevant.has(role.key));
  }, [roles, tenderRoleIds, rows]);

  const load = useCallback(async () => {
    const request = ++latestLoad.current;
    setLoading(true);
    setError(null);
    try {
      const response = await api.getBoard({
        view,
        quick: quick ?? undefined,
        roleId: roleId || undefined,
        ownerUserId: owner === "me" ? (currentUser ?? undefined) : owner || undefined,
        search: search.trim() || undefined,
        hideExpired: !showExpired,
        sort,
        limit: 500,
      });
      if (request !== latestLoad.current) return;
      setRows(response.rows);
      setSummary(response.summary);
      setTotal(response.total);
    } catch (err: any) {
      if (request === latestLoad.current) setError(isNetworkError(err) ? t("tenderBoard.panel.task.networkError") : (err?.message ?? String(err)));
    } finally {
      if (request === latestLoad.current) setLoading(false);
    }
    // `api` is rebuilt on every render by its hook, so it must not be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, quick, roleId, owner, search, showExpired, sort, currentUser, refreshSignal]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!authentication?.customer) return;
    // The BPM workflows, NOT `getProcessDefinitions` — that one lists the maintenance checklists,
    // whose ids the engine cannot start.
    //
    // Keyed by `workflowId`, deliberately not by `definitionId`: a definition only exists once the
    // workflow has been deployed, and the only other thing that deploys is switching event
    // detection on. Filtering on it made "start a process" silently require an unrelated setting —
    // in a fresh worktree, whose BPM database starts empty, it disabled the button outright. The
    // server deploys on the way in now, so an undeployed workflow is a perfectly startable one.
    // Best-effort: on failure the triage row simply offers no start, which is a smaller failure
    // than blocking the whole board on an unrelated call.
    userAPI
      .getBpmSchedules(authentication.customer)
      .then((workflows: any[]) =>
        setDefinitions(
          (workflows ?? [])
            .map((workflow: any) => ({
              key: String(workflow?.workflowId ?? ""),
              text: workflow?.workflowName ?? "",
            }))
            .filter((option) => option.key)
        )
      )
      .catch(() => setDefinitions([]));
  }, [authentication?.customer]);

  useEffect(() => {
    if (!authentication?.customer) return;
    // Names for the owner column and the "Zuweisen an" list. Best-effort: listing users is an
    // administrator's right, and without it the board falls back to the e-mail's local part.
    getUsers(authentication.customer)
      .then((users: any) => {
        if (!Array.isArray(users)) return;
        const names: Record<string, string> = {};
        for (const user of users) {
          if (!user?.email) continue;
          const last = user.lastName ? ` ${String(user.lastName).charAt(0)}.` : "";
          names[user.email] = user.firstName ? `${user.firstName}${last}` : user.email;
        }
        setPeople(names);
      })
      .catch(() => setPeople({}));
    // `getUsers` is rebuilt on every render by its hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  /** Everyone a tender can be assigned to: known users, current owners, and oneself. */
  const peopleOptions = useMemo(() => {
    const emails = new Set<string>(Object.keys(people));
    if (currentUser) emails.add(currentUser);
    rows.forEach((row) => row.ownerUserId && emails.add(row.ownerUserId));
    return Array.from(emails)
      .map((email) => ({ key: email, text: ownerName(email, people) }))
      .sort((a, b) => a.text.localeCompare(b.text, "de"));
  }, [people, currentUser, rows]);

  /** One row changed (from the panel): write it back in place — the list is not reloaded. */
  const replaceRow = useCallback(
    (changed: TenderBoardRow) =>
      setRows((previous) => previous.map((existing) => (existing.tenderId === changed.tenderId ? changed : existing))),
    []
  );

  const openRow = (row: TenderBoardRow) => {
    if (openInNewTab) {
      const url = new URL(window.location.href);
      url.searchParams.set("tender", row.tenderId);
      window.open(url.toString(), "_blank", "noopener");
      return;
    }
    setOpenId(row.tenderId);
  };

  /**
   * Apply one per-tender write to several rows. Every row is tried, a failure on one does not stop
   * the rest, and the result says how many went through — a batch that fails silently on two of
   * twelve rows is worse than one that refuses outright.
   */
  const runOnRows = async (targets: TenderBoardRow[], perRow: (row: TenderBoardRow) => Promise<unknown>) => {
    setBatchBusy(true);
    setError(null);
    try {
      const results = await Promise.allSettled(targets.map(perRow));
      const failed = results.filter((result) => result.status === "rejected") as PromiseRejectedResult[];
      setNotice(
        failed.length
          ? t("tenderBoard.batch.partial", { done: targets.length - failed.length, count: targets.length })
          : t("tenderBoard.batch.done", { count: targets.length })
      );
      if (failed.length) {
        const reason = failed[0].reason;
        setError(isNetworkError(reason) ? t("tenderBoard.panel.task.networkError") : String(reason?.message ?? reason));
      }
      await load();
      if (openId && targets.some((row) => row.tenderId === openId)) setPanelRefresh((value) => value + 1);
    } finally {
      setBatchBusy(false);
    }
  };

  // „Passt“ / „Passt nicht“ from the row's popover. Only the feedback changes (the light never does),
  // so the row is patched in place instead of reloading the board under the open popover.
  const answerFit = async (row: TenderBoardRow, verdict: "fits" | "notFits" | null) => {
    await api.setTenderFitFeedback(row.tenderId, verdict);
    const fitFeedback = verdict
      ? { verdict, score: row.fit?.score ?? null, by: currentUser, at: new Date().toISOString() }
      : null;
    setRows((previous) => previous.map((item) => (item.tenderId === row.tenderId ? { ...item, fitFeedback } : item)));
    if (openId === row.tenderId) setPanelRefresh((value) => value + 1);
  };

  const runDialog = async (open: BoardDialog, value: string) => {
    const perRow: Record<BoardDialog["kind"], ((row: TenderBoardRow) => Promise<unknown>) | null> = {
      dismiss: (row) => api.dismissTender(row.tenderId, value.trim() || undefined),
      handover: (row) => api.handOverTender(row.tenderId, value),
      assign: (row) => api.setTenderOwner(row.tenderId, value),
      startProcess: (row) => api.startTenderProcess(row.tenderId, value, row.responsibleRoleId),
      existingCustomer: null,
    };
    const run = perRow[open.kind];
    if (!run) return;
    await runOnRows(open.rows, run);
    setDialog(null);
    if (open.rows.length > 1) setClearSelection((value) => !value);
  };

  // One clock per render of the list, so every row is measured against the same moment.
  const now = useMemo(() => new Date(), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      name: t("tenderBoard.columns.tender"),
      grow: 3,
      // Must wrap. The table is auto-layout, so a `nowrap` cell does not truncate — it widens the
      // table to fit its longest content, and the columns behind it slide out of view behind a
      // scrollbar macOS does not even draw. Tender titles are long TED strings ("Deutschland –
      // Reinigungs- und Hygienedienste – Unterhalts- und Glasreinigung für das Bundesarchiv in
      // Berlin Lichterfelde"), which pushed the table to 2624 px inside a 1728 px window and left
      // only this column visible. Same reason for the other free-text columns below.
      wrap: true,
      cell: (row: TenderBoardRow) => <TenderCell row={row} people={people} profileNames={profileNames} onFitFeedback={answerFit} />,
    },
    {
      name: t("tenderBoard.columns.status"),
      width: "180px",
      // The open task is authored in the modeller, so its length is not ours to bound.
      wrap: true,
      cell: (row: TenderBoardRow) => <StatusCell row={row} />,
    },
    {
      name: t("tenderBoard.columns.documents"),
      width: "170px",
      wrap: true,
      cell: (row: TenderBoardRow) => <DocumentsCell row={row} />,
    },
    {
      name: t("tenderBoard.columns.owner"),
      width: "160px",
      wrap: true,
      cell: (row: TenderBoardRow) => <OwnerCell row={row} people={people} />,
    },
    {
      name: t("tenderBoard.columns.deadline"),
      width: "150px",
      cell: (row: TenderBoardRow) => <DeadlineCell row={row} now={now} />,
    },
    {
      name: <span className="sr-only">{t("tenderBoard.columns.actions")}</span>,
      width: "56px",
      // Wraps only so the cell does not clip: the hover buttons overlay the cells to their left.
      wrap: true,
      cell: (row: TenderBoardRow) => (
        <RowActions
          row={row}
          currentUser={currentUser}
          busy={batchBusy}
          canStart={definitions.length > 0}
          onTake={() => runOnRows([row], (target) => api.setTenderOwner(target.tenderId, currentUser))}
          onRelease={() => runOnRows([row], (target) => api.setTenderOwner(target.tenderId, null))}
          onRestore={() => runOnRows([row], (target) => api.restoreTender(target.tenderId))}
          onOpen={setDialog}
        />
      ),
    },
  ];

  const openIndex = openId ? rows.findIndex((row) => row.tenderId === openId) : -1;
  const focus = summary?.focus;
  const viewCounts: Record<BoardView, number | undefined> = {
    triage: focus?.views.triage,
    process: focus?.views.inProgress,
    all: focus?.views.all,
  };
  const viewLabel: Record<BoardView, string> = {
    triage: t("tenderBoard.view.triage"),
    process: t("tenderBoard.view.process"),
    all: t("tenderBoard.view.all"),
  };
  const toggleQuick = (key: TenderBoardQuickFilter) => setQuick((current) => (current === key ? null : key));
  const count = (value: number | undefined) => (value ?? 0).toLocaleString("de-DE");

  return (
    <div className={`marginTopM flex flex-col gap-3 ${selected.length ? "pb-24" : ""}`}>
      {/* 1 — Fokuszeile: was heute zu tun ist, statt der Menge, die liegen geblieben ist. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        {/* Nothing until the counts are in — a focus line of zeros reads as "nothing to do". */}
        <p
          className={`text-base text-muted-foreground ${focus ? "" : "invisible"}`}
          data-testid="tender-board-focus"
        >
          {t("tenderBoard.focus.lead")}{" "}
          <button type="button" className="font-semibold text-foreground hover:underline" onClick={() => toggleQuick("new")}>
            {t("tenderBoard.focus.new", { count: focus?.new ?? 0, value: count(focus?.new) })}
          </button>
          {" · "}
          <button
            type="button"
            className="font-semibold text-foreground hover:underline"
            onClick={() => toggleQuick("dueThisWeek")}
          >
            {t("tenderBoard.focus.dueThisWeek", { count: focus?.dueThisWeek ?? 0, value: count(focus?.dueThisWeek) })}
          </button>
          {" · "}
          <button type="button" className="hover:underline" onClick={() => toggleQuick("noDocuments")}>
            {t("tenderBoard.focus.withoutDocuments", { count: focus?.withoutDocuments ?? 0, value: count(focus?.withoutDocuments) })}
          </button>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={load}>
            <RefreshCw size={14} aria-hidden /> {t("tenderBoard.refresh")}
          </Button>
          <Button className="gap-2" onClick={() => setAddOpen(true)}>
            <Plus size={14} aria-hidden /> {t("tenderBoard.add.button")}
          </Button>
        </div>
      </div>

      <AddTenderDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(tender) => {
          setAddOpen(false);
          setNotice(t("tenderBoard.add.created", { title: tender.title, number: tender.publicationNumber }));
          // A new tender has no process yet, so it is in Triage — and searched for by its number,
          // since a search or chip left set would otherwise hide exactly the row just created.
          setView("triage");
          setQuick(null);
          setSearch(tender.publicationNumber);
        }}
      />

      {notice && (
        <div className="flex items-center gap-2 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span className="flex-1">{notice}</span>
          <button type="button" aria-label={t("global.close")} onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}

      {/* 2 + 3 — Schnellfilter und der Schalter für abgelaufene Fristen. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">{t("tenderBoard.quick.label")}</span>
        {QUICK_FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={quick === key}
            onClick={() => toggleQuick(key)}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              quick === key
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background hover:bg-muted"
            }`}
          >
            {t(`tenderBoard.quick.${key}`)}{" "}
            <span className="font-semibold tabular-nums">{count(focus?.[QUICK_COUNT[key]] as number | undefined)}</span>
          </button>
        ))}
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm">
          <Switch checked={showExpired} onCheckedChange={setShowExpired} />
          {t("tenderBoard.expired.show")}
          {!showExpired && (focus?.expiredHidden ?? 0) > 0 && (
            <span className="text-xs text-muted-foreground">
              {t("tenderBoard.expired.hidden", { count: focus?.expiredHidden ?? 0 })}
            </span>
          )}
        </label>
      </div>

      {/* 4 — Filterleiste: Status, Bereich, Owner, Suche, Sortierung. */}
      {/* Divider lines, not a second card: the section around it is already one (Impeccable: nested cards). */}
      <div className="flex flex-wrap items-center gap-2 border-y border-border py-3">
        <div
          role="radiogroup"
          aria-label={t("tenderBoard.view.label")}
          className="inline-flex gap-0.5 rounded-md border border-input bg-background p-0.5"
        >
          {(["triage", "process", "all"] as BoardView[]).map((key) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={view === key}
              onClick={() => setView(key)}
              className={`h-8 rounded px-3 text-sm transition-colors ${
                view === key ? "bg-foreground font-semibold text-background" : "hover:bg-muted"
              }`}
            >
              {viewLabel[key]}{" "}
              <span className="text-xs tabular-nums opacity-70">{count(viewCounts[key])}</span>
            </button>
          ))}
        </div>

        <div style={{ width: 190 }}>
          <Dropdown
            ariaLabel={t("tenderBoard.filter.area")}
            options={[{ key: "", text: t("tenderBoard.role.all") }, ...departmentRoles]}
            selectedKey={roleId}
            onChange={(_e: any, item: any) => setRoleId((item?.key as string) ?? "")}
          />
        </div>

        <div style={{ width: 190 }}>
          <Dropdown
            ariaLabel={t("tenderBoard.filter.owner")}
            options={[
              { key: "", text: t("tenderBoard.filter.ownerAll") },
              ...(currentUser ? [{ key: "me", text: t("tenderBoard.filter.ownerMe") }] : []),
              ...peopleOptions.filter((option) => option.key !== currentUser),
            ]}
            selectedKey={owner}
            onChange={(_e: any, item: any) => setOwner((item?.key as string) ?? "")}
          />
        </div>

        <div className="relative min-w-52 flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus:border-ring"
            placeholder={t("tenderBoard.searchPlaceholder")}
            aria-label={t("tenderBoard.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div style={{ width: 220 }}>
          <Dropdown
            ariaLabel={t("tenderBoard.sort.label")}
            options={[
              { key: "deadline", text: t("tenderBoard.sort.deadline") },
              { key: "newest", text: t("tenderBoard.sort.newest") },
              { key: "fit", text: t("tenderBoard.sort.fit") },
            ]}
            selectedKey={sort}
            onChange={(_e: any, item: any) => item && setSort(item.key as SortKey)}
          />
        </div>
      </div>

      <div className={`flex flex-wrap justify-between gap-2 px-1 text-xs text-muted-foreground ${focus ? "" : "invisible"}`}>
        <span>
          {t("tenderBoard.resultLine", { shown: count(total), of: count(viewCounts[view]), view: viewLabel[view] })}
          {quick && ` · ${t("tenderBoard.filterActive", { filter: t(`tenderBoard.quick.${quick}`) })}`}
        </span>
        {!showExpired && <span>{t("tenderBoard.expired.hiddenNote")}</span>}
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertTriangle size={14} className="mr-1 inline" />
          {error}
        </div>
      )}

      {/* No frame of its own: the section is the card, the table's row lines carry the structure. */}
      <div className="pb-2">
        {loading && rows.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            {t("tenderBoard.loading")}
          </div>
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            keyField="tenderId"
            customStyles={dataTableStyles}
            pagination
            paginationPerPage={20}
            noDataComponent={
              <div className="p-6 text-sm text-muted-foreground">{t("tenderBoard.empty")}</div>
            }
            selectableRows
            clearSelectedRows={clearSelection}
            onSelectedRowsChange={({ selectedRows }: { selectedRows: TenderBoardRow[] }) => setSelected(selectedRows)}
            onRowClicked={(row: TenderBoardRow) => openRow(row)}
          />
        )}
      </div>
      <div className="px-1 text-xs text-muted-foreground">{t("tenderBoard.batch.hint")}</div>

      {/* 10 — Batch-Leiste, solange etwas ausgewählt ist. */}
      <BatchBar
        rows={selected}
        busy={batchBusy}
        onTake={() =>
          runOnRows(selected, (row) => api.setTenderOwner(row.tenderId, currentUser)).then(() =>
            setClearSelection((value) => !value)
          )
        }
        onOpen={setDialog}
        onClear={() => setClearSelection((value) => !value)}
      />

      <BoardActionDialog
        dialog={dialog}
        roles={roles}
        people={peopleOptions}
        definitions={definitions}
        busy={batchBusy}
        onClose={() => setDialog(null)}
        onRun={runDialog}
      />

      <TenderDetailPanel
        tenderId={openId}
        row={openIndex >= 0 ? rows[openIndex] : null}
        position={openIndex >= 0 ? { index: openIndex, total: rows.length } : null}
        onPrevious={openIndex > 0 ? () => setOpenId(rows[openIndex - 1].tenderId) : undefined}
        onNext={openIndex >= 0 && openIndex < rows.length - 1 ? () => setOpenId(rows[openIndex + 1].tenderId) : undefined}
        onClose={() => setOpenId(null)}
        openInNewTab={openInNewTab}
        onOpenInNewTabChange={(value) => {
          setOpenInNewTab(value);
          try {
            window.localStorage.setItem(NEW_TAB_KEY, value ? "1" : "0");
          } catch {
            /* A convenience; without storage it holds for this page only. */
          }
        }}
        currentUser={currentUser}
        people={people}
        definitions={definitions}
        onRowChanged={replaceRow}
        onOpenDialog={setDialog}
        refreshSignal={panelRefresh}
      />
    </div>
  );
}
